/**
 * Tailwind Typography color bindings for prose that renders directly on a section's background.
 *
 * `SectionContainer` publishes `--lp-text` / `--lp-text-muted` (only when the section has a
 * background override) so content can stay readable on a custom color or photo. Plain elements
 * opt in with `text-(--lp-text,<original>)`, but a `.prose` block can't: the typography
 * plugin colors its own children from `--tw-prose-*` custom properties, so an ancestor's `color`
 * never reaches them.
 *
 * These rebind those variables instead of layering `prose-<element>:` utilities on top, for two
 * reasons found the hard way:
 *  - Only the plugin's *element* variants exist (`prose-p:`, `prose-strong:`, …). There is no
 *    `prose-counters:`/`prose-bullets:`/`prose-quotes:`/`prose-code:` variant, so those compiled to
 *    nothing at all and list markers, quotes and inline code stayed dark on a dark section.
 *  - A `prose-strong:` utility outranks the plugin's own `a strong { color: inherit }` rule, so
 *    bold text inside a link lost the link color — on every page, override or not, because the
 *    utility's fallback applies unconditionally.
 * Rebinding the variables leaves the plugin's internal cascade (including `a strong`) intact and
 * reaches every colored part in one place.
 *
 * Every binding keeps `prose-gray`'s own value as the `var()` fallback, so a section with no
 * override resolves to exactly the color it rendered before this existed. `--tw-prose-links` is
 * deliberately not bound — links are owned by `--lp-accent` via the `prose-a:` utility each
 * renderer already applies, which is contrast-corrected separately.
 */

/**
 * Class string binding Tailwind Typography's text colors to the section's resolved text color.
 * Apply alongside `prose prose-gray` on prose that sits directly on the section background — not
 * on prose inside a card or other opaque surface of its own, which keeps its plain gray.
 */
export const PROSE_SECTION_TEXT_VARS = [
  // Full-strength tiers — body copy and the elements the plugin renders at gray-900.
  "[--tw-prose-body:var(--lp-text,#374151)]",
  "[--tw-prose-headings:var(--lp-text,#111827)]",
  "[--tw-prose-bold:var(--lp-text,#111827)]",
  "[--tw-prose-quotes:var(--lp-text,#111827)]",
  "[--tw-prose-code:var(--lp-text,#111827)]",
  "[--tw-prose-kbd:var(--lp-text,#111827)]",
  // Muted tiers — kept a step quieter than body copy, as `prose-gray` has them.
  "[--tw-prose-lead:var(--lp-text-muted,#4b5563)]",
  "[--tw-prose-counters:var(--lp-text-muted,#6b7280)]",
  "[--tw-prose-captions:var(--lp-text-muted,#6b7280)]",
  "[--tw-prose-bullets:var(--lp-text-muted,#d1d5db)]",
].join(" ");

/**
 * Full class string for rich-text prose sitting directly on a section background: typography base,
 * accent-colored links, medium body weight (Satoshi at 400 reads too thin on a landing page), and
 * the {@link PROSE_SECTION_TEXT_VARS} color bindings. Size is left to the caller (`prose-base`,
 * `prose-sm md:prose-base`, ...). Headings and bold set their own weights in the plugin, so
 * `font-medium` only changes body copy.
 */
export const SECTION_PROSE_CLASSES = `prose prose-gray max-w-none prose-a:text-(--lp-accent) prose-a:underline font-medium ${PROSE_SECTION_TEXT_VARS}`;
